import { configureStore, isRejectedWithValue } from "@reduxjs/toolkit";

import { emptySplitApi, previewSplitApi, nonUserApi } from "../Services";

// reducer import
import customizationReducer from "./customizationReducer";
import snackbarReducer from "./snackbarReducer";

import AuthSlice from "views/pages/authentication/Auth.Slice";
import SharedSlice from "./Shared.Slice";
import UserProfileSlice from "views/account/user-profile/ProfileInfo.Slice";
import AlertNotificationSlice from "views/account/user-profile/AlertNotification.Slice";
import AdditionalUsersSlice from "views/account/additional-users/AdditionalUsers.Slice";
import SignatureSlice from "views/account/user-profile/Signature.Slice";
import OrganizationProfileInfoSlice from "views/account/organization-profile/OrganizationProfileInfo.Slice";
import OrganizationAdditionalEmailSlice from "views/account/organization-profile/OrganizationAdditionalEmail.Slice";
import ManageAccountSlice from "views/admin/manage-accounts/ManageAccount.Slice";
import CreateAccountSlice from "views/admin/create-account/CreateAccount.Slice";
import CreateSubscriptionPlanSlice from "views/admin/create-subscription-plan/CreateSubscriptionPlan.Slice"
import UpdateAccountSlice from "views/admin/update-account/UpdateAccount.Slice";
import UpdateFoundationCaSlice from "views/admin/update-foundation-ca/UpdateFoundationCa.Slice";
import UpdateFoundationUsSlice from "views/admin/update-foundation-us/UpdateFoundationUs.Slice";
import UserProjectSlice from "views/user-project/UserProject.Slice";
import SearchEngineSlice from "views/search-engine/common/SearchEngine.Slice";
import IncomeSlice from "views/project-budget/Income.Slice";
import ExpenseSlice from "views/project-budget/Expense.Slice";
import FoundationProfileSlice from "views/foundation-profile/FoundationProfile.Slice";
import FoundationProfileSliceV2 from "views/foundation-profile-v2/FoundationProfile.Slice";
import InquirySlice from "views/document-generator/inquiry/Inquiry.Slice";
import ProposalSlice from "views/document-generator/proposal/Proposal.Slice";
import CustomSlice from "views/document-generator/custom/Custom.Slice";
import GrantSecretarySlice from "views/grant-secretary/GrantSecretary.Slice";
import GrantSecretarySliceV2 from "views/grant-secretary-v2/GrantSecretary.Slice";
import FavoritesSlice from "views/manage-funders/favorites/Favorites.Slice";
import BlockedFundersSlice from "views/manage-funders/blocked-funders/BlockedFunders.Slice";
import CustomDatabaseSlice from "views/manage-funders/custom-databases/CustomDatabase.Slice";
import FoundationProfileCASlice from "views/foundation-profile-ca/FoundationProfile.CA.Slice";
import FoundationProfileCASliceV2 from "views/foundation-profile-ca-v2/FoundationProfile.CA.Slice";
import ManageSubscriptionPlanSlice from "views/admin/manage-subscription-plans/ManageSubscriptionPlan.Slice";
import UpdateSubscriptionPlanSlice from "views/admin/update-subscription-plan/UpdateSubscriptionPlan.Slice";
import ManageCouponSlice from "views/admin/manage-coupons/ManageCoupon.Slice";
import ManageDeadlineSlice from "views/admin/manage-deadlines/ManageDeadline.Slice";
import ManageFeedbackSlice from "views/admin/manage-feedbacks/ManageFeedback.Slice";
import ManageSmartPhrasesSlice from "views/admin/manage-smart-phrases/ManageSmartPhrases.Slice";
import ManageFoundationsSlice from "views/admin/manage-foundations/ManageFoundations.Slice";
import ThankYouLetterSlice from "views/document-generator/thank-you/ThankYou.Slice";
import ImpactReportLetterSlice from "views/document-generator/impact-report/ImpactReport.Slice";
import PublicFoundationProfileSlice from "views/pages/public-profile-page/PublicFoundationProfile.Slice";
import OrganizationStepperSlice from "views/shared/organization-profile-stepper/slice";
import DocumentGeneratorSliceV2 from "views/document-generator-v2/DocumentGenerator.Slice";
import RegistrationStepperSlice from "views/shared/registration-stepper/slice";
import ImportDatabaseStepperSlice from "views/shared/custom-database-import/slice";
import CustomDatabaseSliceV2 from "views/manage-funders-v2/custom-databases-v2/slice";

import SearchEngineV3Slice from "views/search-engine/v3/slice";

const rtkQueryErrorLogger = (api) => (next) => (action) => {

    if (isRejectedWithValue(action)) {
        console.warn("Api Error", action);

        let redirectUrl = null;
        let doRedirect = false;

        const generalError = action.payload?.error;
        
        // 401 - Unauthorized: authentication issue
        // Kick the user back to the login page. Common error for expiration token
        if (action.payload.status === 401) {
            console.error('401 - Unauthorized', action);
            localStorage.clear();
            sessionStorage.setItem("RedirectDueExpiration", "true");
            redirectUrl = '/';
            doRedirect = true;
        }

        // When more than one request is made and the user is blocked by the backend action filter
        if (action.payload.status === 400) {
            const actionIsUnauthorized = action.payload?.data?.message?.search('{"message":"Unauthorized"}'); //returns 0 when found; -1 when not found

            if (actionIsUnauthorized === 0) {
                console.error('401 - Unauthorized', action);
                localStorage.clear();
                sessionStorage.setItem("RedirectDueExpiration", "true");
                redirectUrl = '/';
                doRedirect = true;
            }
        }

        // 403 - Forbidden: permission issue
        // Kick the user back to the dashboard page. User is authenticated but doesn’t have permission.
        if (action.payload.status === 403) {
            console.error('403 - Forbidden', action);
            redirectUrl = '/main';
            doRedirect = true;
            sessionStorage.setItem("RedirectDuePermission", "true");
        }

        // 415 - Unsupported Media Type
        if (action.payload.status === 415) {
            console.error('415 - Unsupported Media Type', action);
        }

        //Check for unauthorized message:
        
        


        // General Errors
        if (generalError != null) {
            console.error('General Errors', action);
        }

        if (doRedirect) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            window.location.href = '/';
        }

        if (redirectUrl != null) {
            window.setTimeout(() => {
                window.location.href = redirectUrl;
            }, 2000);
        }
    }

    return next(action);
};

export default configureStore({
    reducer: {
        customization: customizationReducer,
        snackbar: snackbarReducer,
        authState: AuthSlice,
        sharedState: SharedSlice,
        userProfileState: UserProfileSlice,
        alertNotificationState: AlertNotificationSlice,
        additionalUsersState: AdditionalUsersSlice,
        signatureState: SignatureSlice,
        organizationProfileState: OrganizationProfileInfoSlice,
        organizationAdditionalEmailState: OrganizationAdditionalEmailSlice,
        manageAccountState: ManageAccountSlice,
        manageSubscriptionPlanState: ManageSubscriptionPlanSlice,
        manageCouponState: ManageCouponSlice,
        manageDeadlineState: ManageDeadlineSlice,
        manageFeedbackState: ManageFeedbackSlice,
        manageSmartPhrasesState: ManageSmartPhrasesSlice,
        manageFoundationsState: ManageFoundationsSlice,
        createAccountState: CreateAccountSlice,
        createSubscriptionPlanState: CreateSubscriptionPlanSlice,
        updateSubscriptionPlanState: UpdateSubscriptionPlanSlice,
        updateAccountState: UpdateAccountSlice,
        updateFoundationCaState: UpdateFoundationCaSlice,
        updateFoundationUsState: UpdateFoundationUsSlice,
        userProjectState: UserProjectSlice,
        searchEngineState: SearchEngineSlice,
        incomeState: IncomeSlice,
        expenseState: ExpenseSlice,
        foundationProfileState: FoundationProfileSlice,
        foundationProfileStateV2: FoundationProfileSliceV2,
        inquiryState: InquirySlice,
        proposalState: ProposalSlice,
        customState: CustomSlice,
        grantSecretaryState: GrantSecretarySlice,
        grantSecretaryStateV2: GrantSecretarySliceV2,
        favoritesState: FavoritesSlice,
        blockedFundersState: BlockedFundersSlice,
        customDatabaseState: CustomDatabaseSlice,
        foundationProfileCAState: FoundationProfileCASlice,
        foundationProfileCAStateV2: FoundationProfileCASliceV2,
        thankYouLetterState: ThankYouLetterSlice,
        impactReportLetterState: ImpactReportLetterSlice,
        publicFoundationProfileState: PublicFoundationProfileSlice,
        organizationStepperState: OrganizationStepperSlice,
        documentGeneratorStateV2: DocumentGeneratorSliceV2,
        registrationStepperState: RegistrationStepperSlice,
        importDatabaseStepperState: ImportDatabaseStepperSlice,
        customDatabaseStateV2: CustomDatabaseSliceV2,
        searchEngineStateV3: SearchEngineV3Slice,
        [emptySplitApi.reducerPath]: emptySplitApi.reducer,
        [previewSplitApi.reducerPath]: previewSplitApi.reducer,
        [nonUserApi.reducerPath]: nonUserApi.reducer,
    },
    middleware: (getDefaultMiddleware) => {
        const middlewareList = [rtkQueryErrorLogger, emptySplitApi.middleware, previewSplitApi.middleware, nonUserApi.middleware, ...getDefaultMiddleware({ serializableCheck: false })];
        return middlewareList;
    },
});
